import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { HideComponentWrapper, IconButton, Typography } from "..";
import { IDateRangePickerProps } from "../../models";
import styles from "./DateRangePicker.module.css";
import { DATE_FORMATE, INPUT_DATE_TIME_FORMATE, STANDARD_DATE_FORMAT } from "../../constant";

const DateRangePicker: React.FC<IDateRangePickerProps> = (props: IDateRangePickerProps) => {
	const {
		size = "lg",
		isRangePicker = false,
		format = STANDARD_DATE_FORMAT,
		inputFormat = INPUT_DATE_TIME_FORMATE,
		onDateChange,
		value,
		isDoubleCalender = false,
		defaultMonth,
		defaultMonthLeft,
		defaultMonthRight,
		className = "",
		containerClass = ""
	} = props;
	const [currentMonthLeft, setCurrentMonthLeft] = useState<moment.Moment>(moment().subtract(1, "months").startOf("month"));
	const [currentMonthRight, setCurrentMonthRight] = useState<moment.Moment>(moment().startOf("month"));
	const [startDate, setStartDate] = useState<any>(null);
	const [endDate, setEndDate] = useState<any>(null);
	const [hoveredDate, setHoveredDate] = useState<any>(null);

	useEffect(() => {
		const _startDate = value?.startDate ? moment(value.startDate, inputFormat).format(format) : "";
		const _endDate = value?.endDate ? moment(value.endDate, inputFormat).format(format) : "";
		setStartDate(_startDate);
		setEndDate(_endDate);
		if (!isDoubleCalender) {
			setCurrentMonthLeft(defaultMonth ?? moment().startOf("month"));
		}
		if (defaultMonthLeft) {
			setCurrentMonthLeft(defaultMonthLeft);
		}
		if (defaultMonthRight) {
			setCurrentMonthRight(defaultMonthRight);
		}
	}, []);
	useEffect(() => {
		const _startDate = value?.startDate ? moment(value.startDate, inputFormat).format(format) : "";
		const _endDate = value?.endDate ? moment(value.endDate, inputFormat).format(format) : "";
		setStartDate(_startDate);
		setEndDate(_endDate);
	}, [value]);
	const handlePrevMonthLeft = () => {
		setCurrentMonthLeft(currentMonthLeft.clone().subtract(1, "months"));
	};

	const handleNextMonthLeft = () => {
		// check if the current month is same or the before month of the current month of the right calendar then return null;
		if (
			isDoubleCalender &&
			(currentMonthLeft.isSame(currentMonthRight) || currentMonthLeft.isSame(moment(currentMonthRight).subtract(1, "months").startOf("month")))
		) {
			return;
		}
		setCurrentMonthLeft(currentMonthLeft.clone().add(1, "months"));
	};

	const handlePrevMonthRight = () => {
		if (
			isDoubleCalender &&
			(currentMonthRight.isSame(currentMonthLeft) || currentMonthRight.isSame(moment(currentMonthLeft).add(1, "months").startOf("month")))
		) {
			return;
		}
		setCurrentMonthRight(currentMonthRight.clone().subtract(1, "months"));
	};

	const handleNextMonthRight = () => {
		setCurrentMonthRight(currentMonthRight.clone().add(1, "months"));
	};

	const handleDayClick = (day: number, isLeft: boolean) => {
		if (!isRangePicker) {
			const date = currentMonthLeft.clone().date(day).format(format);
			onDateChange(date, null);
			setEndDate(currentMonthLeft.clone().date(day));
			setHoveredDate(null);
			return null;
		}
		const selectedDate = (isLeft ? currentMonthLeft : currentMonthRight).clone().date(day);
		if (!startDate || (startDate && endDate)) {
			setStartDate(selectedDate);
			setEndDate(null);
			setHoveredDate(null);
		} else if (!endDate && selectedDate.isSameOrAfter(startDate)) {
			setEndDate(selectedDate);
			onDateChange(startDate.format(format), selectedDate.format(format));
		} else if (!endDate && selectedDate.isBefore(startDate)) {
			setEndDate(startDate);
			setStartDate(selectedDate);
			onDateChange(selectedDate.format(format), startDate.format(format));
		}
	};

	const handleDayHover = (day: number, isLeft: boolean) => {
		const hovered = (isLeft ? currentMonthLeft : currentMonthRight).clone().date(day);
		if (startDate && !endDate && hovered.isSameOrAfter(startDate)) {
			setHoveredDate(hovered);
		} else if (startDate && !endDate && hovered.isBefore(startDate)) {
			setHoveredDate(hovered);
		}
	};

	const isSelected = (day: number, isLeft: boolean) => {
		const date = (isLeft ? currentMonthLeft : currentMonthRight).clone().date(day);
		return (startDate && date.isSame(startDate, "day")) || (endDate && date.isSame(endDate, "day"));
	};

	const isInRange = (day: number, isLeft: boolean) => {
		if (!isRangePicker) return false;
		const date = (isLeft ? currentMonthLeft : currentMonthRight).clone().date(day);
		if (!startDate || (!endDate && !hoveredDate)) return false;
		const rangeEnd = endDate || hoveredDate;
		return date.isBetween(startDate, rangeEnd, "day", "[]");
	};

	const blankDaysLeft = new Array(currentMonthLeft.startOf("month").day()).fill(null);
	const blankDaysRight = new Array(currentMonthRight.startOf("month").day()).fill(null);

	const renderCalendar = (currentMonth: moment.Moment, isLeft: boolean) => {
		const daysInMonth = currentMonth.daysInMonth();

		return (
			<div className={`${styles.calendar} ${styles["size_" + size]} ${className}`}>
				<div className={styles.header}>
					<IconButton onClick={isLeft ? handlePrevMonthLeft : handlePrevMonthRight} variant={"secondary"} buttonType="text">
						<CaretLeft weight="bold" />
					</IconButton>
					<Typography type="text" size={size === "lg" ? "md" : size} fontFamily="semibold">
						{currentMonth.format("MMMM YYYY")}
					</Typography>
					<IconButton onClick={isLeft ? handleNextMonthLeft : handleNextMonthRight} variant={"secondary"} buttonType="text">
						<CaretRight weight="bold" />
					</IconButton>
				</div>
				<div className={styles.daysGrid}>
					{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
						<div key={day} className={styles.dayLabel}>
							{day}
						</div>
					))}
					{(isLeft ? blankDaysLeft : blankDaysRight).map((_, index) => (
						<div key={index} className={styles.emptyDay}></div>
					))}
					{Array.from({ length: daysInMonth }, (_, index) => (
						<button
							key={index + 1}
							className={`${styles.day_btn} ${isSelected(index + 1, isLeft) ? styles.selected : ""} ${isInRange(index + 1, isLeft) ? styles.inRange : ""}`}
							onClick={() => handleDayClick(index + 1, isLeft)}
							onMouseEnter={() => handleDayHover(index + 1, isLeft)}
						>
							{index + 1}
						</button>
					))}
				</div>
			</div>
		);
	};

	return (
		<div className={styles.dateRangePicker}>
			<div className={`${styles.calendarsContainer} ${containerClass}`}>
				{renderCalendar(currentMonthLeft, true)}
				<HideComponentWrapper hidden={!isDoubleCalender}>
					<div className={styles.calendar_divider}></div>
					{renderCalendar(currentMonthRight, false)}
				</HideComponentWrapper>
			</div>
		</div>
	);
};

export default DateRangePicker;
